import { NavLink } from 'react-router-dom';
import {
  IconLoader,
  formatDate,
  IconSelected,
  IconAttention,
  IconClock,
  IconBlock,
  DateInput,
  DateInputType,
  InputText,
} from '@ui/src';
import InfoControls from './parts/info-controls';
import DocumentInfoComments from './parts/info-comments';
import { CustomerStatus } from '../../../hoc/common/types';
import DocumentPreviewImage from './parts/document-preview';
import { CustomerDocumentStatus } from '../../../hoc/common/documents/types';
import { DocumentInfoProps } from './types';

import cn from './styles.module.css';

const getTransactionLink = (type: string) => {
  let transactionLink = '';

  switch (type) {
    case 'INCOMING':
      transactionLink = '/transactions/incoming';
      break;
    case 'OUTGOING':
      transactionLink = '/transactions/outgoing';
      break;
    case 'INTERNAL':
      transactionLink = '/transactions/internal';
      break;
    case 'GENERAL_LEDGER':
      transactionLink = '/transactions/general-ledger';
      break;
    case 'CURRENCY_EXCHANGE':
      transactionLink = '/transactions/exchange';
      break;
    case 'NOSTRO':
      transactionLink = '/transactions/nostro';
      break;
    default:
      break;
  }

  return transactionLink;
};

const DocumentInfoContent = ({
  info,
  isLoading,
  setDeletePopupVisible,
  onMove,
  withoutControls,
  imgLink,
  fileIsLoading,
  downloadInProgress,
  onDownload,
  sendComment,
  deleteIsVisible,
  moveInProgress,
  commentsVisible,
  commentInProgress,
  onChangeDocumentExpDate,
  updateAllowed,
  textareaRef,
  changeDateError,
  updateInProgress,
  documentTransactionType,
  allowedCategories,
  documentName,
  setDocumentName,
  nameError,
  renameAvailable,
  status,
  users,
}: DocumentInfoProps): React.JSX.Element => {
  let content;

  if (isLoading) {
    content = (
      <div className={cn.documentInfoLoaderWrapper}>
        <IconLoader className={cn.documentInfoLoader} />
      </div>
    );
  } else {
    let statusIcon: React.JSX.Element;

    if (info?.status === CustomerDocumentStatus.ACCEPTED) {
      statusIcon = (
        <div className={`${cn.documentRowAccepted} ${cn.documentInfoAccepted}`}>
          <IconSelected
            className={cn.documentRowStatusIcon}
            height={9}
            width={13}
          />
        </div>
      );
    } else if (info?.status === CustomerDocumentStatus.EXPIRED) {
      statusIcon = (
        <div className={cn.documentInfoExpired}>
          <IconAttention />
        </div>
      );
    } else if (info?.status === CustomerDocumentStatus.REJECTED) {
      statusIcon = (
        <div className={cn.documentInfoRejected}>
          <IconBlock />
        </div>
      );
    } else {
      statusIcon = <div className={cn.documentRowNew} />;
    }

    let imageContent;

    if (fileIsLoading || downloadInProgress) {
      imageContent = <IconLoader />;
    } else {
      imageContent = (
        <a
          href={imgLink}
          target="_blank"
          rel="noreferrer"
          aria-label="Document link"
        >
          <DocumentPreviewImage />
        </a>
      );

      if (info?.upload?.mimeType?.includes('image')) {
        imageContent = (
          <a href={imgLink} target="_blank" rel="noreferrer">
            <img
              src={imgLink}
              alt={info?.name}
              className={
                fileIsLoading || downloadInProgress || !imgLink
                  ? cn.imageHidden
                  : ''
              }
            />
          </a>
        );
      }
    }

    content = (
      <>
        <div className={cn.documentInfoBlock}>
          <div className={cn.documentInfoPreview}>{imageContent}</div>
          {!withoutControls && (
            <InfoControls
              group={info?.category}
              documentCategoryId={info?.documentCategoryId}
              setDeletePopupVisible={setDeletePopupVisible}
              onMove={onMove}
              moveInProgress={moveInProgress}
              onDownload={onDownload}
              deleteIsVisible={deleteIsVisible}
              allowedCategories={allowedCategories}
              status={status}
            />
          )}
        </div>
        <div className={cn.documentInfoBlock}>
          <div className={cn.documentInfoStatusWrapper}>
            <div className={cn.documentInfoStatus}>
              {info?.status}
              {statusIcon}
            </div>
            <div className={cn.documentInfoDates}>
              <div
                className={`${cn.documentInfoDate} ${
                  info?.status === CustomerDocumentStatus.EXPIRED
                    ? cn.documentDateExpired
                    : ''
                }`}
              >
                <div>Exp. date:</div>
                {info?.expirationDate ? formatDate(info?.expirationDate) : '-'}
                {info?.status === CustomerDocumentStatus.EXPIRED && (
                  <IconClock className={cn.expiredIcon} />
                )}
              </div>
              <div className={cn.documentInfoDate}>
                <div>Loaded:</div>
                {info?.createdAt ? formatDate(info?.createdAt, true) : '-'}
              </div>
            </div>
          </div>
          {updateAllowed && status !== CustomerStatus.CLOSED && (
            <div className={cn.changeExpDateWrapper}>
              <DateInput
                type={DateInputType.PSEUDO}
                placeholder={
                  updateInProgress ? 'Loading...' : 'Change expiration date'
                }
                value={info?.expirationDate || ''}
                onChange={(date) => onChangeDocumentExpDate(date.toISOString())}
                withoutHidePlaceholder
                withoutCustomSelector
              />
            </div>
          )}
          {changeDateError && (
            <div className={cn.updateError}>{changeDateError}</div>
          )}
        </div>
        {renameAvailable && (
          <div className={cn.documentInfoBlock}>
            <div className={cn.documentInfoCommentsTitle}>Document Name</div>
            <InputText
              name="documentName"
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={!info || status === CustomerStatus.CLOSED}
              error={nameError}
            />
          </div>
        )}
        {info?.transactionId && documentTransactionType && (
          <div className={cn.documentInfoBlock}>
            <NavLink
              to={`${getTransactionLink(
                documentTransactionType,
              )}/${info?.transactionId}`}
              target="_blank"
              className={cn.transactionLink}
            >{`Transaction: ${info?.transactionId}`}</NavLink>
          </div>
        )}
        <DocumentInfoComments
          comments={info?.commentsList}
          sendComment={sendComment}
          commentInProgress={commentInProgress}
          documentId={info?.id}
          textareaRef={textareaRef}
          commentsVisible={commentsVisible}
          status={status}
          users={users}
        />
      </>
    );
  }

  return content;
};

export default DocumentInfoContent;
